/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/react-in-jsx-scope */
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import _ from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChargerEV, CompanyBuilding, ErrorWarningCircle, Success } from '../../assets/icons';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { IconSize } from '../../constant/IconSize.constant';
import { NUMBER } from '../../constant/Number.constant';
import { useAuth, USER_ROLE } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { useAllowedFeatures } from '../../hooks/useFeaturePersonalization';
import { useFetchChargersQuery } from '../../services/charger.api';
import { useFetchLocationsQuery } from '../../services/location.api';
import {
  useDownloadPaymentsCSVReportMutation,
  useFetchRfidClientHistoryQuery,
  useGenerateNyserdaEnergyUseReportAsyncMutation,
  useGenerateNyserdaStatusReportAsyncMutation,
  useGenerateReportSyncMutation,
  useGenerateSessionIntervalsReportAsyncMutation,
  useGenerateSessionsReportAsyncMutation,
  useGenerateStatisticsReportAsyncMutation,
} from '../../services/report.api';
import { Charger, Location } from '../../stores/types';
import { Category, IRange, Reports, ReportType } from '../../stores/types/report.interface';
import { convertToUTCEndOfDay, convertToUTCStartOfDay, formatDate } from '../../utils/Date.Util';
import {
  ChargerDropdownItem,
  getAllChargers,
  getSelectedChargersCount,
  isAllChargersSelected,
  MAX_CHARGER_SELECTION,
  SELECTION_VALUES,
} from '../../utils/paymentsReport.utils';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  CustomDatePicker,
  DateTimePicker,
  Divider,
  Dropdown,
  DropdownType,
  Icon,
  Label,
  LabelType,
  Pill,
  Snackbar,
} from '../_ui';
import { Form, FormQueryResult } from '../_ui/Form.component';
import { DATE_RANGE } from '../_ui/date-picker/Custom-Date-Picker.component';
import { MonthPicker } from '../_ui/date-picker/Multiple-Month-Picker.component';
import { AlertPosition, AlertType } from '../_ui/snack-bar/Snack-Bar.component';
import { ReportContent } from './ReportContent.component';

interface ChargerPill {
  id: string;
  label: string;
}

interface CompanyItem {
  id: string;
  selected: boolean;
}

const HOME_CHARGERS_LOCATION_ID = 'home-chargers';

/** Stable fallbacks for RTK selectFromResult — inline `?? []` creates a new ref every render when skipped/loading. */
const EMPTY_LOCATIONS: Location[] = [];
const EMPTY_CHARGERS: Charger[] = [];
const EMPTY_CHARGERS_LIST: ChargerDropdownItem[] = [];

const getChargerDropdownLabel = (charger: Charger): string =>
  charger.displayName ?? charger.name ?? charger.id;

export const Report = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const today = new Date();
  today.setMonth(today.getMonth() - 1);
  const { LAST_WEEK, LAST_MONTH, CUSTOM_RANGE } = DATE_RANGE;
  const { allowNyserdaReports } = useAllowedFeatures();

  const [generateReportSync, generateReportSyncResult] = useGenerateReportSyncMutation();
  const [generateStatisticsReportAsync, generateStatisticsReportAsyncResult] =
    useGenerateStatisticsReportAsyncMutation();
  const [generateSessionsReportAsync, generateSessionsReportAsyncResult] =
    useGenerateSessionsReportAsyncMutation();
  const [generateSessionIntervalsReportAsync, generateSessionIntervalsReportAsyncResult] =
    useGenerateSessionIntervalsReportAsyncMutation();
  const [generateNyserdaEnergyUseReportAsync, generateNyserdaEnergyUseReportAsyncResult] =
    useGenerateNyserdaEnergyUseReportAsyncMutation();
  const [generateNyserdaStatusReportAsync, generateNyserdaStatusReportAsyncResult] =
    useGenerateNyserdaStatusReportAsyncMutation();

  const [chargers, setChargers] = useState<string[]>([]);
  const [categoryType, setCategoryType] = useState<Category | null>(null);
  const [reportType, setReportType] = useState<null | ReportType>(null);
  const [userFilterEnabled, setUserFilterEnabled] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<IRange>();
  const [reportData, setReportData] = useState({
    companyIds: [],
    chargerIds: [],
    months: [],
    selectedMonth: null,
  } as Reports);

  const [downloadCSVReport] = useDownloadPaymentsCSVReportMutation();
  const [chargerDropdownList, setChargerDropdownList] = useState<ChargerDropdownItem[]>([]);
  const { isMultiViewActive, companies, userScope, companyId } = useCompany();
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const allChargerSelected = chargerDropdownList.length > 0 && chargerDropdownList[0].selected;

  const selectedCompany = useMemo(
    () => companies?.find((company) => company.id === companyId),
    [companies, companyId],
  );
  const isHomeChargingPartner = selectedCompany?.homeChargingPartner === true;

  const getReportTypeForCSV = (): 'users' | 'chargers' | 'sessions' => {
    if (reportType === ReportType.USERS) return 'users';
    if (reportType === ReportType.CHARGERS) return 'chargers';
    return 'sessions';
  };

  const handleDownloadCSV = async () => {
    if (!reportData.selectedMonth || !reportData.companyIds?.length) return;

    const startDate = new Date(reportData.selectedMonth);
    const endDate = new Date(reportData.selectedMonth);
    endDate.setMonth(endDate.getMonth() + 1);

    // Prepare users parameter - don't include if "all" is selected
    let usersToPass: string[] | undefined;
    if (reportType === ReportType.SESSIONS && selectedUsers.length > 0) {
      // If "all" is in selectedUsers, don't pass users parameter (means all users)
      if (!selectedUsers.includes(SELECTION_VALUES.ALL)) {
        usersToPass = selectedUsers.map((user) => (user === 'Auto-start' ? 'AUTOSTART' : user));
      }
    }

    // Prepare chargers parameter - don't include if "all chargers" is selected
    let chargersToPass: string[] | undefined;
    if (reportType === ReportType.CHARGERS && chargers.length > 0) {
      // If all chargers are selected, don't pass chargerIds parameter (means all chargers)
      if (!allChargerSelected) {
        chargersToPass = chargers;
      }
    }

    try {
      await downloadCSVReport({
        reportType: getReportTypeForCSV(),
        startDate,
        endDate,
        companyIds: reportData.companyIds,
        ...(chargersToPass && { chargerIds: chargersToPass }),
        ...(usersToPass && { users: usersToPass }),
        userScope,
      });
    } catch (error) {
      Snackbar.show({
        message: t('error_downloading_csv'),
        position: AlertPosition.BOTTOM_LEFT,
        alertType: AlertType.ERROR,
        duration: 5000,
      });
    }
  };

  const { locations, refetch: refetchLocation } = useFetchLocationsQuery(
    { scope: userScope!, include: SELECTION_VALUES.ALL },
    {
      skip: !userScope || isHomeChargingPartner,
      selectFromResult: (endPoint) => ({
        locations: endPoint?.data?.entities ?? EMPTY_LOCATIONS,
      }),
    },
  );

  const { partnerChargers } = useFetchChargersQuery(
    { scope: userScope },
    {
      skip: !userScope || !isHomeChargingPartner,
      selectFromResult: (endPoint) => ({
        partnerChargers: endPoint?.data?.entities ?? EMPTY_CHARGERS,
      }),
    },
  );

  const { data: userEmails } = useFetchRfidClientHistoryQuery(
    {
      month: reportData.selectedMonth
        ? formatDate(reportData.selectedMonth, 'MMMM').toUpperCase()
        : undefined,
      year: reportData.selectedMonth ? formatDate(reportData.selectedMonth, 'yyyy') : undefined,
    },
    {
      skip: !userFilterEnabled || !reportData.selectedMonth,
    },
  );

  const userEmailListDropdown = useMemo(() => {
    if (!userEmails || !Array.isArray(userEmails)) return [];
    return [
      {
        id: 'users',
        label: 'Users',
        children: userEmails.map((email) => ({
          id: email,
          label: email,
          selected: selectedUsers.includes(email),
        })),
      },
    ];
  }, [userEmails, selectedUsers]);

  const chargerPillsToShow: ChargerPill[] = [];

  const chargersList = useMemo<ChargerDropdownItem[]>(() => {
    if (isHomeChargingPartner) {
      if (partnerChargers.length === 0) {
        return EMPTY_CHARGERS_LIST;
      }

      const children = partnerChargers.map((charger: Charger) => ({
        id: charger.id,
        label: getChargerDropdownLabel(charger),
        selected: chargers.includes(charger.id),
      }));

      return [
        {
          id: HOME_CHARGERS_LOCATION_ID,
          label: t('home_chargers'),
          children,
          selected: children.every((child) => child.selected),
        },
      ];
    }

    return locations
      .map((location): ChargerDropdownItem | null => {
        const children = location.chargers?.map((charger: Charger) => {
          return {
            id: charger.id,
            label: charger.displayName,
            selected: chargers.includes(charger.id),
          };
        });

        if (children?.length === 0) {
          return null;
        }

        return {
          id: location.id,
          label: location.name,
          children: children || [],
          selected: children?.length ? children?.every((child) => child.selected) : false,
        };
      })
      .filter((location): location is ChargerDropdownItem => location !== null);
  }, [chargers, isHomeChargingPartner, locations, partnerChargers, t]);

  const allChargerList = useMemo(() => {
    // count total chargers
    const totalChargers = chargersList.reduce(
      (sum: number, location) => sum + (location?.children?.length || 0),
      0,
    );

    // "All" is selected if every charger is selected
    const allChargersFlag = totalChargers > 0 && chargers.length === totalChargers;

    const list = _.cloneDeep(chargersList);
    const all = {
      id: SELECTION_VALUES.ALL,
      label: t(SELECTION_VALUES.ALL_CHARGERS),
      selected: allChargersFlag,
      children: [],
    };
    list.unshift(all);
    return list;
  }, [chargersList, chargers, t]);

  useEffect(() => {
    setChargerDropdownList(allChargerList);
  }, [allChargerList]);

  const unselectAllAfterQuerySuccess = useCallback(() => {
    const newLocations = allChargerList.map((location) => {
      return {
        id: location?.id,
        label: location?.label,
        children: location?.children?.length
          ? location?.children?.map((child) => {
              return {
                id: child.id,
                label: child.label,
                selected: false,
              };
            })
          : [],
        selected: false,
      };
    });
    setChargerDropdownList(newLocations);
  }, [allChargerList]);

  const resetStates = useCallback(() => {
    setChargers([]);
    setSelectedCompanies([]);
    setReportType(isHomeChargingPartner ? ReportType.FULL_SESSION : null);
    setCategoryType(isHomeChargingPartner ? Category.SESSIONS : null);
    setDateRange(undefined);
    setReportData({
      chargerIds: [],
      months: [],
      selectedMonth: null,
      ...(isHomeChargingPartner ? { report: ReportType.FULL_SESSION } : {}),
    });
    unselectAllAfterQuerySuccess();
  }, [isHomeChargingPartner, unselectAllAfterQuerySuccess]);

  useEffect(() => {
    setChargers([]);
    setSelectedCompanies([]);
    setReportType(isHomeChargingPartner ? ReportType.FULL_SESSION : null);
    setCategoryType(isHomeChargingPartner ? Category.SESSIONS : null);
    setDateRange(undefined);
    setReportData({
      chargerIds: [],
      months: [],
      selectedMonth: null,
      ...(isHomeChargingPartner ? { report: ReportType.FULL_SESSION } : {}),
    });
    setChargerDropdownList([]);
    if (!isHomeChargingPartner) {
      refetchLocation();
    }
  }, [companyId, isHomeChargingPartner, refetchLocation]);

  useEffect(() => {
    if (!isHomeChargingPartner) return;

    setCategoryType(Category.SESSIONS);
    setReportType(ReportType.FULL_SESSION);
    setReportData((prev) => ({
      ...prev,
      report: ReportType.FULL_SESSION,
    }));
  }, [isHomeChargingPartner]);

  useEffect(() => {
    if (isMultiViewActive) {
      const allCompanyIds = companies?.map((company) => company.id) || [];
      setReportData((prev) => ({
        ...prev,
        companyIds:
          selectedCompanies.length === companies?.length ? allCompanyIds : selectedCompanies,
      }));
    } else {
      setReportData((prev) => ({
        ...prev,
        companyIds: companyId ? [companyId] : [],
      }));
    }
  }, [selectedCompanies, companies, isMultiViewActive, companyId]);

  const categories = [
    {
      id: Category.CHARGERS,
      label: t('chargers'),
      secondaryLabel: t('charger_info'),
      selected: categoryType === Category.CHARGERS,
    },
    {
      id: Category.SESSIONS,
      label: t('overview_sessions'),
      secondaryLabel: t('session_info'),
      selected: categoryType === Category.SESSIONS,
    },
    {
      id: Category.PAYMENTS,
      label: t('payments'),
      secondaryLabel: t('payment_report_info'),
      selected: categoryType === Category.PAYMENTS,
    },
  ];

  const categoryOptions = () => {
    if (isHomeChargingPartner) {
      return categories.filter((cat) => cat.id === Category.SESSIONS);
    }

    // TODO: Remove this filter when payments report is supported in multi-company view
    const baseCategories = isMultiViewActive
      ? categories.filter((cat) => cat.id !== Category.PAYMENTS)
      : categories;

    // At the moment NYSERDA reports are only visible for support users.
    if (allowNyserdaReports) {
      return [
        ...baseCategories,
        {
          id: Category.FINANCIAL_PROGRAMS,
          label: t('financial_programs'),
          secondaryLabel: t('session_info'),
          selected: categoryType === Category.FINANCIAL_PROGRAMS,
        },
      ];
    }
    return [...baseCategories];
  };
  const reportTypeOptions = (type: string) => {
    if (type === Category.SESSIONS) {
      const sessionReportTypes = [
        {
          id: ReportType.FULL_SESSION,
          label: t('full_session'),
          secondaryLabel: t('full_session_info'),
          selected: reportType === ReportType.FULL_SESSION,
        },
        {
          id: ReportType.INTERVALS,
          label: t('interval'),
          secondaryLabel: t('interval_info'),
          selected: reportType === ReportType.INTERVALS,
        },
      ];

      if (isHomeChargingPartner) {
        return sessionReportTypes.filter((option) => option.id === ReportType.FULL_SESSION);
      }

      return sessionReportTypes;
    }
    if (type === Category.PAYMENTS) {
      return [
        {
          id: ReportType.CHARGERS,
          label: t('chargers'),
          secondaryLabel: t('charger_info'),
          selected: reportType === ReportType.CHARGERS,
        },
        {
          id: ReportType.USERS,
          label: t('users'),
          secondaryLabel: t('user_info'),
          selected: reportType === ReportType.USERS,
        },
        {
          id: ReportType.SESSIONS,
          label: t('sessions'),
          secondaryLabel: t('session_info'),
          selected: reportType === ReportType.SESSIONS,
        },
      ];
    }
    if (type === Category.FINANCIAL_PROGRAMS) {
      return [
        {
          id: ReportType.NYSERDA_ENERGY_USE,
          label: t('nyserda_energy_use'),
          selected: reportType === ReportType.NYSERDA_ENERGY_USE,
        },
        {
          id: ReportType.NYSERDA_STATUS_HISTORY,
          label: t('nyserda_status_history'),
          selected: reportType === ReportType.NYSERDA_STATUS_HISTORY,
        },
      ];
    }
    return [
      {
        id: ReportType.CHARGER_STATS,
        label: t('charger_stats'),
        secondaryLabel: t('charger_stats_info'),
        selected: reportType === ReportType.CHARGER_STATS,
      },
    ];
  };

  const handleCategoryType = (category: { id: Category }) => {
    setChargers([]);
    setChargerDropdownList([]);
    setReportType(null);
    setCategoryType(category?.id as Category);
    setUserFilterEnabled(false);
    setSelectedUsers([]);
    setChargers([]);
    setReportData({
      ...reportData,
      chargerIds: [],
      report: undefined,
    });
  };

  const handleReportType = (report: { id: ReportType }) => {
    // Reset charger selection if report type doesn’t allow filtering
    if (![ReportType.FULL_SESSION, ReportType.INTERVALS].includes(report?.id)) {
      setChargers([]);
      setReportData({ ...reportData, chargerIds: [] });
    }

    // Toggle user dropdown visibility
    setUserFilterEnabled(report?.id === ReportType.SESSIONS);
    if (report?.id !== ReportType.SESSIONS) {
      setSelectedUsers([]);
    }

    setReportType(report?.id as ReportType);
    setReportData({
      ...reportData,
      report: report?.id,
    });
  };

  const renderCardTitle = () => {
    return (
      <div className='flex flex-row items-baseline gap-2'>
        <Label type={LabelType.H4} color={ColorType.BLACK2} text={t('run_report')} />
      </div>
    );
  };
  const handleChargersChange = (items: ChargerDropdownItem[]) => {
    const allChargers = getAllChargers(items);
    const selectedChargers = allChargers.filter((ch) => ch.selected);
    const newChargers = selectedChargers.map((ch) => ch.id);
    const isAllSelected = allChargers.length > 0 && allChargers.every((ch) => ch.selected);

    const updatedItems = items.map((item) =>
      item.id === SELECTION_VALUES.ALL ? { ...item, selected: isAllSelected } : item,
    );

    setChargerDropdownList(updatedItems);
    if (!isAllSelected && selectedChargers.length > MAX_CHARGER_SELECTION) {
      return;
    }

    setChargers(newChargers);
    setReportData((prev) => ({
      ...prev,
      chargerIds: isAllSelected ? [SELECTION_VALUES.ALL] : newChargers,
    }));
  };

  const queryResult: FormQueryResult & {
    error?: FetchBaseQueryError | SerializedError;
    reset: () => void;
  } = useMemo(() => {
    if (isMultiViewActive) {
      switch (reportType) {
        case ReportType.CHARGER_STATS:
          return generateStatisticsReportAsyncResult;
        case ReportType.FULL_SESSION:
          return generateSessionsReportAsyncResult;
        case ReportType.INTERVALS:
          return generateSessionIntervalsReportAsyncResult;
        case ReportType.NYSERDA_ENERGY_USE:
          return generateNyserdaEnergyUseReportAsyncResult;
        case ReportType.NYSERDA_STATUS_HISTORY:
          return generateNyserdaStatusReportAsyncResult;
        default:
          return {
            isSuccess: false,
            isError: false,
            isLoading: false,
            error: undefined,
            reset: () => {},
          };
      }
    } else {
      return generateReportSyncResult;
    }
  }, [
    isMultiViewActive,
    generateStatisticsReportAsyncResult,
    generateSessionsReportAsyncResult,
    generateSessionIntervalsReportAsyncResult,
    generateNyserdaEnergyUseReportAsyncResult,
    generateNyserdaStatusReportAsyncResult,
    generateReportSyncResult,
    reportType,
  ]);

  /**
   * If no error is specified, this will default to a success toast
   * based on the context of single or multi company. Otherwise, it
   * generates an error toast based on the type of error.
   *
   * @param syncReportRoutedToEmail The type of error which occurred.
   * @param error The type of error which occurred.
   */
  const displayResultToast = useCallback(
    (syncReportRoutedToEmail: boolean, error?: 'NO_RECORDS' | 'UNKNOWN') => {
      let snackbarMessage: ReactElement;
      if (!error) {
        snackbarMessage =
          !isMultiViewActive && !syncReportRoutedToEmail ? (
            <>
              <Label
                text={t('report_request_submitted')}
                type={LabelType.LABEL_S_MEDIUM}
                color={ColorType.BLACK}
              />
              <Label
                text={t('report_download')}
                type={LabelType.LABEL_S_MEDIUM}
                color={ColorType.BLACK}
              />
            </>
          ) : (
            <Label
              text={t('emailed_report_request')}
              type={LabelType.LABEL_S_MEDIUM}
              color={ColorType.BLACK}
            />
          );
      } else if (error === 'NO_RECORDS') {
        snackbarMessage = (
          <Label
            text={t('failed_report_message')}
            type={LabelType.LABEL_S_MEDIUM}
            color={ColorType.BLACK}
          />
        );
      } else {
        snackbarMessage = (
          <Label
            text={t('unknown_error_generating_report')}
            type={LabelType.LABEL_S_MEDIUM}
            color={ColorType.BLACK}
          />
        );
      }

      Snackbar.show({
        message: snackbarMessage,
        position: AlertPosition.BOTTOM_LEFT,
        alertType: AlertType.DEFAULT,
        duration: 5000,
        messageLabelType: LabelType.LABEL_S_MEDIUM,
        messageLabelColor: ColorType.BLACK,
        icon: !error ? Success : ErrorWarningCircle,
        height: 'h-auto',
      });
    },
    [isMultiViewActive, t],
  );

  const handleSaveClick = async () => {
    const requestedAt = new Date();
    captureAnalyticsEvent(AnalyticsEvent.REPORT_GENERATE_EVENT);
    // Handle Payments category CSV download
    if (categoryType === Category.PAYMENTS) {
      try {
        await handleDownloadCSV();
        displayResultToast(false);
        return;
      } catch (error) {
        console.error('Error downloading CSV:', error);
        Snackbar.show({
          message: t('error_downloading_csv'),
          position: AlertPosition.BOTTOM_LEFT,
          alertType: AlertType.ERROR,
          duration: 5000,
        });
        return;
      }
    }

    // Note that this must be kept in sync with queryResultToMonitor above
    if (isMultiViewActive) {
      switch (reportType) {
        case ReportType.CHARGER_STATS:
          generateStatisticsReportAsync({
            scope: userScope!,
            companyIds: reportData.companyIds!,
            months: reportData.months,
            requestedAt,
          });
          break;
        case ReportType.FULL_SESSION:
          generateSessionsReportAsync({
            scope: userScope!,
            companyIds: reportData.companyIds!,
            fromSessionCompletedDate: reportData.fromSessionCompletedDate!,
            toSessionCompletedDate: reportData.toSessionCompletedDate!,
            requestedAt,
          });
          break;
        case ReportType.INTERVALS:
          generateSessionIntervalsReportAsync({
            scope: userScope!,
            companyIds: reportData.companyIds!,
            fromSessionCompletedDate: reportData.fromSessionCompletedDate!,
            toSessionCompletedDate: reportData.toSessionCompletedDate!,
            requestedAt,
          });
          break;
        case ReportType.NYSERDA_ENERGY_USE:
          generateNyserdaEnergyUseReportAsync({
            scope: userScope!,
            companyIds: reportData.companyIds!,
            reportStartDate: reportData.fromSessionCompletedDate!,
            reportEndDate: reportData.toSessionCompletedDate!,
            requestedAt,
          });
          break;
        case ReportType.NYSERDA_STATUS_HISTORY:
          generateNyserdaStatusReportAsync({
            scope: userScope!,
            companyIds: reportData.companyIds!,
            reportStartDate: reportData.fromSessionCompletedDate!,
            reportEndDate: reportData.toSessionCompletedDate!,
            requestedAt,
          });
          break;
        default:
          break;
      }
    } else {
      generateReportSync({
        scope: userScope!,
        reportInfo: reportData,
        requestedAt,
      });
    }
  };

  const handleFilterItemClick = (clickedItem: ChargerPill) => {
    const newList = chargerDropdownList.map((item) => {
      let isAllChildrenChecked = true;
      const newChildren = item.children?.map((child) => {
        const newSelected = child.id === clickedItem.id ? false : child.selected;
        if (!newSelected) isAllChildrenChecked = false;
        return {
          ...child,
          selected: newSelected,
        };
      });
      return {
        ...item,
        selected: item.id === SELECTION_VALUES.ALL ? item.selected : isAllChildrenChecked,
        children: newChildren,
      };
    });
    setChargerDropdownList(newList);
    const updatedChargers: string[] = [];
    newList.forEach((loc) => {
      loc.children?.forEach((child) => {
        if (child.selected) {
          updatedChargers.push(child.id);
        }
      });
    });

    setChargers(updatedChargers);
    setReportData((prev) => ({
      ...prev,
      chargerIds: updatedChargers,
    }));
  };

  const unselectAllChargers = () => {
    const newList = chargerDropdownList.map((item) => ({
      ...item,
      selected: false,
      children: item.children?.map((child) => ({
        ...child,
        selected: false,
      })),
    }));

    setChargerDropdownList(newList);
    setChargers([]);
    setReportData((prev) => ({
      ...prev,
      chargerIds: [],
    }));
  };

  const renderSelectedChargers = () => {
    const isReadOnly =
      reportType !== null && [ReportType.USERS, ReportType.SESSIONS].includes(reportType);

    if (allChargerSelected) {
      return (
        <Pill
          iconLeft={ChargerEV}
          key={SELECTION_VALUES.ALL_CHARGERS}
          label={t(SELECTION_VALUES.ALL_CHARGERS)}
          hasCloseButton={!isReadOnly}
          onClick={isReadOnly ? undefined : unselectAllChargers}
          translationOn
        />
      );
    }
    if (allChargerSelected) {
      return (
        <Pill
          iconLeft={ChargerEV}
          key={SELECTION_VALUES.ALL_CHARGERS}
          label={t(SELECTION_VALUES.ALL_CHARGERS)}
          hasCloseButton
          onClick={unselectAllChargers}
          translationOn
        />
      );
    }
    chargerDropdownList?.forEach((ele) => {
      ele.children?.forEach((child) => {
        if (child.selected) {
          chargerPillsToShow.push({
            id: child.id,
            label: child.label,
          });
        }
      });
    });
    return chargerPillsToShow.map((item) => {
      return (
        // eslint-disable-next-line react/react-in-jsx-scope
        <Pill
          iconLeft={ChargerEV}
          key={item.id}
          label={item.label}
          hasCloseButton
          onClick={() => handleFilterItemClick(item)}
          translationOn
        />
      );
    });
  };

  const renderCategory = () => {
    return (
      <div className='flex flex-col gap-2'>
        <Label text={t('category')} type={LabelType.LABEL_M} />
        <Dropdown
          placeholder={t('select_category')}
          placeholderLabelType={LabelType.BODY3_G4}
          headerWidth={340}
          items={categoryOptions()}
          type={DropdownType.SELECT}
          contentDivHeight={336}
          contentDivWidth={324}
          minWidth={324}
          onItemClick={(item: { id: Category }) => handleCategoryType(item)}
          labelType={LabelType.BODY3}
          labelTypeHighlighted={LabelType.BODY3}
          chevdownIconHighlightColor={ColorType.GREY6}
          headerHighLightClassName='bg-grey1 border-grey3 rounded'
          disabled={isHomeChargingPartner}
        />
      </div>
    );
  };
  const renderReportType = () => {
    return (
      <div className='flex flex-col gap-[8px]'>
        <Label text={t('report_type')} type={LabelType.LABEL_M} />
        {!categoryType && <Label text={t('report_type_placeholder')} type={LabelType.BODY3_G4} />}
        {categoryType && (
          <Dropdown
            placeholder={t('select_report_type')}
            placeholderLabelType={LabelType.BODY3_G4}
            headerWidth={340}
            contentDivHeight={336}
            contentDivWidth={324}
            minWidth={324}
            items={reportTypeOptions(categoryType)}
            type={DropdownType.SELECT}
            onItemClick={(item: { id: ReportType }) => handleReportType(item)}
            labelType={LabelType.BODY3}
            labelTypeHighlighted={LabelType.BODY3}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded'
            disabled={isHomeChargingPartner}
          />
        )}
      </div>
    );
  };

  interface UserItem {
    id: string;
    label: string;
    selected: boolean;
  }

  const handleUserSelection = (items: UserItem[]) => {
    const allUserIds = userEmailListDropdown?.[0]?.children?.map((u: UserItem) => u.id) || [];
    const allItem = items.find((i) => i.id === SELECTION_VALUES.ALL);
    const normalItems = items.filter((i) => i.id !== SELECTION_VALUES.ALL);
    const chosen = normalItems.filter((i) => i.selected).map((i) => i.id);

    // If "All users" explicitly selected, select all
    if (allItem?.selected && !selectedUsers.includes(SELECTION_VALUES.ALL)) {
      setSelectedUsers([SELECTION_VALUES.ALL, ...allUserIds]);
      return;
    }

    // If "All users" unselected, clear all
    if (!allItem?.selected && selectedUsers.includes(SELECTION_VALUES.ALL)) {
      setSelectedUsers([]);
      return;
    }

    // If all normal users manually selected, set all
    if (allUserIds.length > 0 && allUserIds.every((id) => chosen.includes(id))) {
      setSelectedUsers([SELECTION_VALUES.ALL, ...allUserIds]);
    } else {
      setSelectedUsers(chosen);
    }
  };

  const renderUsersDropdown = () => {
    if (!userFilterEnabled) return null;

    const allUserIds = userEmailListDropdown?.[0]?.children?.map((u) => u.id) || [];
    const isAllSelected =
      selectedUsers.includes(SELECTION_VALUES.ALL) ||
      (allUserIds.length > 0 && allUserIds.every((id) => selectedUsers.includes(id)));

    const userItems =
      userEmailListDropdown?.length > 0
        ? [
            {
              id: SELECTION_VALUES.ALL,
              label: t(SELECTION_VALUES.ALL_USERS),
              selected: isAllSelected,
            },
            ...userEmailListDropdown[0].children.map((u) => ({
              id: u.id,
              label: u.label,
              selected: selectedUsers.includes(u.id),
            })),
          ]
        : [
            {
              id: SELECTION_VALUES.ALL,
              label: t(SELECTION_VALUES.ALL_USERS),
              selected: isAllSelected,
            },
          ];

    const getPlaceholder = () => {
      if (selectedUsers.length === 0) return t('select_user');
      if (isAllSelected) return t(SELECTION_VALUES.ALL_USERS);
      if (selectedUsers.length === 1) return selectedUsers[0];
      return `${selectedUsers.length} users`;
    };

    return (
      <div className='flex flex-col gap-[8px]'>
        <Label text={t('users')} type={LabelType.LABEL_M} />
        <Dropdown
          placeholder={getPlaceholder()}
          placeholderLabelType={LabelType.BODY3_G4}
          headerWidth={340}
          items={userItems}
          contentDivHeight={336}
          contentDivWidth={324}
          minWidth={324}
          type={DropdownType.CHECKBOX}
          onItemClick={handleUserSelection}
          labelType={LabelType.BODY3}
          labelTypeHighlighted={LabelType.BODY3}
          chevdownIconHighlightColor={ColorType.GREY6}
          headerHighLightClassName='bg-grey1 border-grey3 rounded'
          disabled={!reportData.selectedMonth}
        />
      </div>
    );
  };

  // Stable reference so CustomDatePicker's React.memo isn't defeated by a fresh callback
  // on every render (which would re-render the date range picker and remount its icons).
  const selectDate = useCallback((option: string, date: Date[]) => {
    setDateRange({
      option,
      date,
    });
    setReportData((prev) => ({
      ...prev,
      fromSessionCompletedDate: convertToUTCStartOfDay(date[0]),
      toSessionCompletedDate: convertToUTCEndOfDay(date[1]),
    }));
  }, []);
  const dateChanged = (newMonths: Array<Date>) => {
    setReportData({
      ...reportData,
      months: newMonths,
    });
  };

  const handleMonthChange = useCallback((month: Date | null) => {
    setReportData((prev) => ({
      ...prev,
      selectedMonth: month ?? null,
    }));
    setSelectedUsers([]);
  }, []);

  // Stable reference so DateTimePicker's React.memo isn't defeated by a fresh
  // `new Date()` on every render (which would remount the month input + ChevronDown icon).
  const monthPickerMaxDate = useMemo(() => new Date(), []);

  const renderMonthDropdown = () => {
    const hasSelectedMonth = !!reportData.selectedMonth;
    return (
      <div className='flex flex-col gap-[8px] w-[340px] relative'>
        <DateTimePicker
          showMonthYearPicker
          format='MMMM yyyy'
          showDefaultDate={false}
          customWidthClass='w-[340px]'
          customFontClass='font-inter text-[14px] font-normal'
          customFontColor={hasSelectedMonth ? 'text-black' : 'text-grey4'}
          onChange={handleMonthChange}
          maxDate={monthPickerMaxDate}
          iconSize={IconSize.SIZE_24x24}
          disabled={false}
        />
      </div>
    );
  };

  const renderTimeRange = () => {
    return (
      <div className='flex flex-col gap-[8px] relative'>
        <Label
          text={categoryType === Category.PAYMENTS ? t('month') : t('time_range')}
          type={LabelType.LABEL_M}
        />
        {!reportType && categoryType !== Category.PAYMENTS && (
          <Label text={t('time_range_placeholder')} type={LabelType.BODY3_G4} />
        )}
        {!reportType && categoryType === Category.PAYMENTS && (
          <Label text={t('month_picker_placeholder')} type={LabelType.BODY3_G4} />
        )}
        <div className='flex'>
          {reportType &&
            reportType !== ReportType.CHARGER_STATS &&
            categoryType !== Category.PAYMENTS && (
              <CustomDatePicker
                optionList={[LAST_WEEK, LAST_MONTH, CUSTOM_RANGE]}
                format='MMM d,yyyy'
                onChange={selectDate}
                selectedOption={dateRange?.option ? dateRange?.option : ''}
                showHighligtHeader={false}
                placeholder='Select time range'
              />
            )}
          {reportType && reportType === ReportType.CHARGER_STATS && (
            <MonthPicker months={reportData.months} onChange={dateChanged} maxDate={today} />
          )}
          {categoryType === Category.PAYMENTS && reportType && renderMonthDropdown()}
        </div>
        {reportType === ReportType.CHARGER_STATS && (
          <div>
            <Label text={t('date_picker_text1')} type={LabelType.BODY3} color={ColorType.GREY6} />
            <Label text={t('date_picker_text2')} type={LabelType.BODY3} color={ColorType.GREY6} />
          </div>
        )}
      </div>
    );
  };
  const renderReportContents = () => {
    return (
      <div className='flex flex-col gap-[8px]'>
        <Label text={t('report_contents')} type={LabelType.LABEL_M} />
        {!reportType && <Label text={t('report_contents_placeholder')} type={LabelType.BODY3_G4} />}
        {reportType && <ReportContent data={reportType} />}
      </div>
    );
  };

  const onSaveValidation = () => {
    // Charger reports
    if (categoryType === Category.CHARGERS) {
      const selectedChargersCount = getSelectedChargersCount(chargerDropdownList);
      const allChargersSelected = isAllChargersSelected(chargerDropdownList);

      // disable if > 50 chargers
      if (!allChargersSelected && selectedChargersCount > MAX_CHARGER_SELECTION) {
        return false;
      }

      if (isMultiViewActive) {
        return selectedCompanies.length > 0 && reportData.months.length > 0 && !!reportType;
      }

      // enable if at least one charger or all selected
      const hasValidChargerSelection = allChargersSelected || selectedChargersCount > 0;

      return hasValidChargerSelection && reportData.months.length > 0 && !!reportType;
    }

    // Payments reports
    if (categoryType === Category.PAYMENTS) {
      if (isMultiViewActive) {
        return selectedCompanies.length > 0 && reportData.selectedMonth !== null && !!reportType;
      }

      if (reportType === ReportType.USERS) {
        return reportData?.selectedMonth !== null && !!reportType;
      }

      if (reportType === ReportType.SESSIONS) {
        return reportData?.selectedMonth !== null && selectedUsers.length > 0 && !!reportType;
      }

      if (reportType === ReportType.CHARGERS) {
        const selectedChargersCount = getSelectedChargersCount(chargerDropdownList);
        const allChargersSelected = isAllChargersSelected(chargerDropdownList);

        // disable if > 50 chargers
        if (!allChargersSelected && selectedChargersCount > MAX_CHARGER_SELECTION) {
          return false;
        }

        const hasValidChargerSelection = allChargersSelected || selectedChargersCount > 0;

        return hasValidChargerSelection && reportData.selectedMonth !== null && !!reportType;
      }
      return false;
    }

    // NYSERDA
    if (categoryType === Category.FINANCIAL_PROGRAMS) {
      const selectedChargersCount = getSelectedChargersCount(chargerDropdownList);
      const allChargersSelected = isAllChargersSelected(chargerDropdownList);

      // disable if > 50 chargers
      if (!allChargersSelected && selectedChargersCount > MAX_CHARGER_SELECTION) {
        return false;
      }

      if (isMultiViewActive) {
        return selectedCompanies.length > 0 && !!reportType && !!dateRange?.option;
      }

      return (
        !!reportType && !!dateRange?.option && (allChargersSelected || selectedChargersCount > 0)
      );
    }

    // Sessions / other
    if (isMultiViewActive) {
      return selectedCompanies.length > 0 && !!categoryType && !!reportType && !!dateRange?.option;
    }

    const selectedChargersCount = getSelectedChargersCount(chargerDropdownList);
    const allChargersSelected = isAllChargersSelected(chargerDropdownList);

    // disable if > 50 chargers
    if (!allChargersSelected && selectedChargersCount > MAX_CHARGER_SELECTION) {
      return false;
    }

    const hasValidChargerSelection = allChargersSelected || selectedChargersCount > 0;

    return hasValidChargerSelection && !!categoryType && !!reportType && !!dateRange?.option;
  };

  const renderButtons = () => {
    return (
      <div className='flex flex-row-reverse gap-2'>
        <Button
          label={t('Run')}
          type={ButtonType.PRIMARY}
          disabled={!onSaveValidation()}
          size={ButtonSize.SMALL}
          onClick={handleSaveClick}
          isSumbit
        />
      </div>
    );
  };

  const handleQuerySuccess = useCallback(
    (syncReportRoutedToEmail: boolean) => {
      displayResultToast(syncReportRoutedToEmail);
      queryResult.reset();
      resetStates();
    },
    [displayResultToast, queryResult, resetStates],
  );

  const companyList = useMemo(() => {
    if (companies) {
      const dropdownOptions = companies.map((company) => {
        return {
          id: company.id,
          key: company.id,
          label: company.name,
          selected: selectedCompanies.indexOf(company.id) !== -1,
          disabled:
            selectedCompanies.length >= NUMBER.MAX_COMPANIES &&
            selectedCompanies.indexOf(company.id) === -1,
        };
      });

      if (auth.role !== USER_ROLE.SUPPORT) {
        dropdownOptions.unshift({
          id: SELECTION_VALUES.ALL,
          key: SELECTION_VALUES.ALL,
          label: t(SELECTION_VALUES.ALL_COMPANIES),
          selected: dropdownOptions.every(({ selected }) => selected),
          disabled: false,
        });
      }
      return dropdownOptions;
    }

    return [];
  }, [auth.role, companies, selectedCompanies, t]);

  const handleAllCompaniesSelect = (item: { id: string; selected: boolean }) => {
    if (item.id === SELECTION_VALUES.ALL && item.selected) {
      const allCompanyIds = companies?.map((company) => company.id) || [];
      setSelectedCompanies(allCompanyIds);
      const allUserIds = userEmailListDropdown?.[0]?.children?.map((user) => user.id) || [];
      setSelectedUsers([SELECTION_VALUES.ALL, ...allUserIds]);
    } else if (item.id === SELECTION_VALUES.ALL && !item.selected) {
      setSelectedCompanies([]);
      setSelectedUsers([]);
    }
  };

  const onCompanySelected = (items: CompanyItem[]) => {
    const allItem = items.find((i) => i.id === SELECTION_VALUES.ALL);

    // Handle the "All companies"
    if (allItem) {
      handleAllCompaniesSelect({ id: allItem.id, selected: allItem.selected });
    }
    const filterCompanies = items.filter((item) => item.selected).map((item) => item.id);
    setSelectedCompanies(filterCompanies);
  };

  const onCompanyPillSelected = (id: string) => {
    const newSelectedCompanies = selectedCompanies!.filter((ids: any) => ids !== id) as string[];
    setSelectedCompanies(newSelectedCompanies);
  };

  const onClearCompanies = () => {
    setSelectedCompanies([]);
  };

  const renderSelectedCompanies = (isAllSelected: boolean) => {
    if (isAllSelected) {
      return (
        <Pill
          iconLeft={CompanyBuilding}
          key={SELECTION_VALUES.ALL}
          label={t(SELECTION_VALUES.ALL_COMPANIES)}
          hasCloseButton
          onClick={() => {
            setSelectedCompanies([]);
          }}
          translationOn
        />
      );
    }
    return (
      <div className='flex flex-row gap-2 flex-wrap mt-1'>
        {companyList
          ?.filter((company) => company.selected)
          .map((pill) => {
            return (
              <Pill
                iconLeft={CompanyBuilding}
                key={pill.id}
                label={pill.label}
                hasCloseButton
                onClick={() => onCompanyPillSelected(pill.id)}
                translationOn
              />
            );
          })}
      </div>
    );
  };

  const noCompaniesSelected = () => {
    return (
      <div className='flex flex-col gap-2 items-center my-4'>
        <Icon src={CompanyBuilding} size={IconSize.SIZE_48x48} />
        <Label text={t('no_companies_selected')} type={LabelType.H4} color={ColorType.GREY5} />
        <Label
          text={t('no_companies_sub_label')}
          type={LabelType.BODY2}
          color={ColorType.GREY5}
          className='text-center'
        />
      </div>
    );
  };

  const renderAllCompaniesDropdown = () => {
    const isAllSelected = companyList
      .filter((i) => i.id !== SELECTION_VALUES.ALL)
      .every(({ selected }) => selected);
    if (companyList && companyList.length > 0) {
      return (
        <div className='flex flex-col max-w-[340px] gap-2'>
          <div className='flex flex-col gap-2'>
            <Label text={t('companies')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Dropdown
              placeholder={t('select_companies')}
              placeholderLabelType={LabelType.BODY3_G4}
              type={DropdownType.CHECKBOX}
              headerWidth='340px'
              contentDivWidth={324}
              minWidth={324}
              items={companyList}
              onItemClick={onCompanySelected}
              handleAllSelect={handleAllCompaniesSelect}
              contentDivHeight={336}
              disabled={selectedCompanies.length >= NUMBER.MAX_COMPANIES}
              showFooter
            />
          </div>
          {!isAllSelected && (
            <div className='flex flex-row justify-between'>
              <Label
                type={LabelType.BODY3}
                color={ColorType.GREY5}
                text={`${selectedCompanies.length}/${NUMBER.MAX_COMPANIES} ${t('selected')}`}
              />
              <button
                type='button'
                className='text-sm text-brand2 hover:underline cursor-pointer whitespace-nowrap'
                onClick={() => onClearCompanies()}
              >
                {t('clear')}
              </button>
            </div>
          )}
          {renderSelectedCompanies(isAllSelected)}
          {selectedCompanies.length === 0 && noCompaniesSelected()}
        </div>
      );
    }
    return null;
  };

  const renderChargerDropdown = () => {
    return (
      <div className='flex flex-col w-full max-w-[340px] gap-2'>
        <Label text={t('pricing_chargers')} type={LabelType.LABEL_M} />

        <Dropdown
          placeholder={t('pricing_chargers_holder')}
          placeholderLabelType={LabelType.BODY3_G4}
          headerWidth='340px'
          items={chargerDropdownList}
          type={DropdownType.CHECKBOX_TREE}
          onItemClick={(items: ChargerDropdownItem[]) => handleChargersChange(items)}
          showFooter
          contentDivHeight={336}
          contentDivWidth={324}
          minWidth={324}
          disabled={
            reportType === null ||
            ![
              ReportType.CHARGER_STATS,
              ReportType.FULL_SESSION,
              ReportType.INTERVALS,
              ReportType.CHARGERS,
              ReportType.NYSERDA_ENERGY_USE,
              ReportType.NYSERDA_STATUS_HISTORY,
            ].includes(reportType) ||
            [ReportType.USERS, ReportType.SESSIONS].includes(reportType)
          }
        />

        {!isAllChargersSelected(chargerDropdownList) && (
          <div className='flex items-center justify-between'>
            <span className='text-sm text-grey5'>
              {getSelectedChargersCount(chargerDropdownList)}/{MAX_CHARGER_SELECTION}
              {` ${t('selected')}`}
            </span>
            <button
              type='button'
              className='text-sm text-brand2 hover:underline cursor-pointer whitespace-nowrap'
              onClick={() => unselectAllChargers()}
            >
              {t('clear')}
            </button>
          </div>
        )}

        <div className='relative w-full min-h-[160px] mt-2'>
          {chargerDropdownList && getSelectedChargersCount(chargerDropdownList) > 0 ? (
            <div className='flex flex-wrap gap-2 max-h-[350px] overflow-y-auto pt-2'>
              {renderSelectedChargers()}
            </div>
          ) : (
            <div className='absolute left-0 right-0 flex flex-col items-center text-center pt-4'>
              <Icon src={ChargerEV} size={IconSize.SIZE_48x48} />
              <Label text={t('no_chargers_selected')} type={LabelType.H4} color={ColorType.GREY5} />
              <Label
                text={t('reports_chargers_info')}
                type={LabelType.BODY2}
                color={ColorType.GREY5}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const prevReportTypeRef = useRef<ReportType | null>(null);
  useEffect(() => {
    const prev = prevReportTypeRef.current;
    const toggleList = (list: ChargerDropdownItem[] | undefined, selected: boolean) =>
      (list ?? []).map((loc) => ({
        ...loc,
        selected,
        children: loc.children?.map((c) => ({ ...c, selected })) ?? [],
      }));

    if (categoryType === Category.PAYMENTS) {
      if (
        reportType !== null &&
        [ReportType.SESSIONS, ReportType.USERS].includes(reportType) &&
        prev !== reportType
      ) {
        // Select all chargers
        const allSelected = (allChargerList ?? []).map((location) => ({
          ...location,
          selected: true,
          children:
            location?.children?.map((child) => ({
              ...child,
              selected: true,
            })) ?? [],
        }));
        setChargerDropdownList(allSelected);
        setReportData((pre) => ({
          ...pre,
          chargerIds: [SELECTION_VALUES.ALL],
        }));
      }

      if (reportType === ReportType.CHARGERS && prev !== ReportType.CHARGERS) {
        const next = toggleList(chargerDropdownList ?? allChargerList, false).map((loc) =>
          loc.id === SELECTION_VALUES.ALL ? { ...loc, selected: false } : loc,
        );
        setChargerDropdownList(next);
        setReportData((p) => ({ ...p, chargerIds: [] }));
        setChargers([]);
      }
    }

    if (
      categoryType === Category.FINANCIAL_PROGRAMS &&
      reportType !== null &&
      [ReportType.NYSERDA_ENERGY_USE, ReportType.NYSERDA_STATUS_HISTORY].includes(reportType) &&
      prev !== reportType
    ) {
      const next = toggleList(chargerDropdownList ?? allChargerList, false).map((loc) =>
        loc.id === SELECTION_VALUES.ALL ? { ...loc, selected: false } : loc,
      );
      setChargerDropdownList(next);
      setReportData((p) => ({ ...p, chargerIds: [] }));
      setChargers([]);
    }

    if (
      categoryType === Category.SESSIONS &&
      reportType !== null &&
      [ReportType.FULL_SESSION, ReportType.INTERVALS].includes(reportType) &&
      prev !== reportType
    ) {
      const next = toggleList(chargerDropdownList ?? allChargerList, false);
      setChargerDropdownList(next);
      setReportData((p) => ({ ...p, chargerIds: [] }));
      setChargers([]);
    }

    prevReportTypeRef.current = reportType;
  }, [categoryType, reportType, chargerDropdownList, allChargerList]);

  return (
    <div>
      <Card OnRenderTitle={renderCardTitle} className='gap-6'>
        <Form
          onSubmit={handleSaveClick}
          queryResult={queryResult}
          onQuerySuccess={() => {
            const data = queryResult?.data as { asyncResponse?: boolean } | undefined;
            const syncReportRoutedToEmail = data?.asyncResponse ?? false;
            handleQuerySuccess(syncReportRoutedToEmail);
          }}
          onQueryFailed={() => {
            if ((queryResult.error as FetchBaseQueryError)?.status === 404) {
              displayResultToast(false, 'NO_RECORDS');
            } else {
              displayResultToast(false, 'UNKNOWN');
            }
            queryResult.reset();
          }}
        >
          <div className='flex flex-col gap-6'>
            <div className='flex flex-row gap-6'>
              <div
                className={`flex flex-col gap-6 border-r border-grey3 pr-6 ${
                  reportType === ReportType.CHARGER_STATS ? 'w-[600px]' : 'w-[364px]'
                }`}
              >
                {renderCategory()}
                {renderReportType()}
                <div className='w-full z-40'>{renderTimeRange()}</div>
                {renderUsersDropdown()}
              </div>
              <div className='flex flex-col gap-6'>
                {isMultiViewActive ? renderAllCompaniesDropdown() : renderChargerDropdown()}
              </div>
            </div>
            <Divider />
            {renderReportContents()}
            {renderButtons()}
          </div>
        </Form>
      </Card>
    </div>
  );
};
